import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DollarSign, Landmark, Calendar, ArrowLeft, Plus, Lock, User } from "lucide-react";
import { toast } from "react-toastify";
import "./RegistrationStyles.css";

const url = process.env.REACT_APP_BACKEND_URL;
const FIXED_FEE = 10.00;

const CustomerRegistrationFees = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [banks, setBanks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    bankname: '',
    amount: FIXED_FEE,
    payment_date: new Date().toISOString().split('T')[0],
    description: '',
    customer: customerId
  });

  useEffect(() => {
    fetchData();
    fetchBanks();
  }, [customerId]);

  const fetchData = async () => {
    try {
      const [feesRes, customerRes] = await Promise.all([
        axios.get(`${url}/api/registrationfee/customer/${customerId}`),
        axios.get(`${url}/api/customer/${customerId}`)
      ]);

      setFees(feesRes.data.data || []);
      setCustomer(customerRes.data.data);
    } catch (error) {
      toast.error("Failed to load registration fees");
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${url}/api/bank`);
      setBanks(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load banks");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.bankname) {
      toast.error("Please select a bank");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${url}/api/registrationfee`, {
        ...formData,
        amount: FIXED_FEE
      });

      if (response.data.success) {
        toast.success(`Registration fee of $${FIXED_FEE} added successfully!`);
        setFormData({
          bankname: '',
          amount: FIXED_FEE,
          payment_date: new Date().toISOString().split('T')[0],
          description: '',
          customer: customerId
        });
        setShowForm(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add registration fee");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const calculateTotal = () => {
    return fees.reduce((sum, fee) => sum + (fee.amount || 0), 0);
  };

  if (loading) return <div className="loading">Loading registration fees...</div>;

  return (
    <div className="registration-container">
      <button onClick={() => navigate('/registration/customers')} className="back-btn" style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={16} /> Back to Customers
      </button>

      <div className="header">
        <h2>
          <DollarSign size={24} /> 
          Registration Fees - {customer?.fullname}
        </h2>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="add-button"
        >
          <Plus size={16} /> {showForm ? 'Hide Form' : 'Add New Fee'}
        </button>
      </div>

      {/* Summary Card */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea, #764ba2)', 
        color: 'white', 
        padding: '1.5rem', 
        borderRadius: '12px', 
        margin: '2rem 0',
        display: 'flex',
        justifyContent: 'space-around',
        boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Fees Paid</span>
          <strong style={{ fontSize: '1.5rem' }}>{formatCurrency(calculateTotal())}</strong>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Number of Payments</span>
          <strong style={{ fontSize: '1.5rem' }}>{fees.length}</strong>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Fee per Payment</span>
          <strong style={{ fontSize: '1.5rem' }}>${FIXED_FEE}</strong>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div style={{ 
          background: '#f7fafc', 
          padding: '2rem', 
          borderRadius: '12px', 
          marginBottom: '2rem',
          border: '2px solid #e2e8f0'
        }}>
          <h3 style={{ marginBottom: '1.5rem', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DollarSign size={20} /> Add New Registration Fee (Fixed: ${FIXED_FEE})
          </h3>
          
          {/* Fixed Fee Info */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea20, #764ba220)',
            border: '1px solid #667eea40',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#4a5568',
            fontSize: '0.95rem'
          }}>
            <Lock size={16} color="#667eea" />
            <span>Registration fee is fixed at <strong>${FIXED_FEE}</strong> and cannot be changed</span>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            
            {/* Fixed Fee Display */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                Registration Fee * (Fixed)
              </label>
              <div style={{ 
                background: '#f0f0f0', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#4a5568'
              }}>
                <DollarSign size={16} color="#4caf50" />
                <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2c3e50' }}>{FIXED_FEE}.00</span>
                <Lock size={14} style={{ marginLeft: 'auto', color: '#718096' }} />
                <small style={{ color: '#718096' }}>(Fixed amount - auto-applied)</small>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                Select Bank *
              </label>
              <select
                name="bankname"
                value={formData.bankname}
                onChange={handleInputChange}
                required
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
              >
                <option value="">Select Bank</option>
                {banks.map(bank => (
                  <option key={bank._id} value={bank._id}>
                    {bank.bankname}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                Payment Date *
              </label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleInputChange}
                required
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                Description (Optional)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Enter any notes about this payment"
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                type="submit"
                disabled={submitting}
                className="add-button"
                style={{ flex: 1, padding: '1rem' }}
              >
                {submitting ? "Processing..." : `Pay $${FIXED_FEE} Registration Fee`}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="toggle-button"
                style={{ flex: 1, padding: '1rem' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <table className="registration-table">
        <thead>
          <tr>
            <th>Receipt No</th>
            <th>Bank</th>
            <th>Amount</th>
            <th>Payment Date</th>
            <th>Description</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {fees.length === 0 ? (
            <tr>
              <td colSpan="6" className="no-data">
                No registration fees found for this customer
              </td>
            </tr>
          ) : (
            fees.map((fee) => (
              <tr key={fee._id}>
                <td>
                  <div className="customer-info">
                    <DollarSign size={14} color="#4caf50" />
                    <span style={{ fontWeight: '600' }}>#{fee.no}</span>
                  </div>
                </td>
                <td>{fee.bankname?.bankname}</td>
                <td style={{ fontWeight: '600', color: '#2c3e50' }}>{formatCurrency(fee.amount)}</td>
                <td>
                  <div className="customer-info">
                    <Calendar size={14} />
                    {new Date(fee.payment_date).toLocaleDateString()}
                  </div>
                </td>
                <td>{fee.description || '-'}</td>
                <td>
                  <span className={`status-badge ${fee.status}`}>
                    {fee.status === "active" ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Summary Footer */}
      {fees.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '2rem'
        }}>
          <div>
            <span style={{ color: '#718096' }}>Total Payments: </span>
            <strong style={{ color: '#2c3e50' }}>{fees.length}</strong>
          </div>
          <div>
            <span style={{ color: '#718096' }}>Total Amount: </span>
            <strong style={{ color: '#4caf50' }}>{formatCurrency(calculateTotal())}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerRegistrationFees;